import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { AppMaterialModule } from '../../../app-material.module';
import { RfxMetaDataRequest } from '../../../models/RfxMetaDataRequest';
import { XrefRelationshipModel } from '../../../models/XrefRelationshipModel';
import { RequestService } from '../../../services/request/request.service';
import { ConversionTarget } from '../../requests/xref-list/xref-list.component';
import { EmailTemplate } from './template';
import { SplitService } from '../../../services/split/split.service';

export interface EmailTemplateData {
  rfxHeaderKey: number;
  rfxMetaData: RfxMetaDataRequest;
  selectedConversionTarget?: ConversionTarget;
  incompleteProductCount: number;
  products: XrefRelationshipModel[];
  monthsOldVerificationToKeep: number;
}

export interface EmailTemplateResult {
  success: boolean;
  rfxHeaderKey: number;
}

@Component({
  selector: 'app-email-template-dialog',
  templateUrl: './email-template-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    AppMaterialModule,
    FormsModule,
    ClipboardModule,
    ReactiveFormsModule,
  ],
  styleUrls: ['./email-template-dialog.component.scss'],
})
export class EmailTemplateDialogComponent {
  readonly data = inject<EmailTemplateData>(MAT_DIALOG_DATA);

  @ViewChild('copyTooltip') copyTooltip: MatTooltip;
  selfDialogRef: MatDialogRef<EmailTemplateDialogComponent>;
  requestService: RequestService;
  snackBar: MatSnackBar;

  protected emailTemplate: string = EmailTemplate.generateTemplate(this.data);
  public automatedSendEnabled = false;

  protected targetEmailFormControl: FormControl<string> = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);

  protected isLoading$ = new BehaviorSubject<boolean>(false);

  protected sendIsDisabled$ = combineLatest([
    this.targetEmailFormControl.valueChanges,
    this.isLoading$,
  ]).pipe(
    map(([_, isLoading]) => {
      return !this.targetEmailFormControl.valid || isLoading;
    }),
    startWith(true),
    distinctUntilChanged(),
  );

  constructor(
    _selfDialogRef: MatDialogRef<EmailTemplateDialogComponent>,
    _requestService: RequestService,
    _snackBar: MatSnackBar,
    _splitService: SplitService,
  ) {
    this.selfDialogRef = _selfDialogRef;
    this.requestService = _requestService;
    this.snackBar = _snackBar;

    if (_splitService.isOn('RFX_Email_Automation')) {
      this.automatedSendEnabled = true;
    }
  }

  closeModal() {
    this.selfDialogRef.close(null);
  }

  sent() {
    this.requestService
      .sendRfx(
        this.data.rfxHeaderKey,
        this.data.selectedConversionTarget?.sourceId,
        this.data.selectedConversionTarget?.sourceType,
      )
      .then(result =>
        this.selfDialogRef.close({
          success: true,
          rfxHeaderKey: result,
        } satisfies EmailTemplateResult),
      )
      .catch(_ =>
        this.snackBar.open('Error saving request send', null, {
          duration: 2000,
        }),
      );
  }

  sendEmail() {
    this.isLoading$.next(true);
    this.requestService
      .sendRfxEmail(
        this.data.rfxHeaderKey,
        this.targetEmailFormControl.value,
        this.data.monthsOldVerificationToKeep,
        this.data.selectedConversionTarget?.sourceId,
        this.data.selectedConversionTarget?.sourceType,
      )
      .then(result =>
        this.selfDialogRef.close({
          success: true,
          rfxHeaderKey: result,
        } satisfies EmailTemplateResult),
      )
      .catch(_ =>
        this.snackBar.open('Error sending email', null, {
          duration: 2000,
        }),
      );
  }

  downloadExcel(
    rfxMetaData: RfxMetaDataRequest,
    products: XrefRelationshipModel[],
  ) {
    if (products.length === 0 || !rfxMetaData.competingSourceType) {
      return;
    }
    const excelData = this.createExcelRowData(products);

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cross Reference Data');

    const fileName = `${
      rfxMetaData.incumbentDisplayValue
    }_${rfxMetaData.competingSecondaryDisplayValue}_Conversion Detail_${
      new Date().toISOString().split('T')[0]
    }.xlsx`;

    this.writeWorkbook(wb, fileName);
  }

  onCopyButtonClick(copyStatus: boolean) {
    if (copyStatus) {
      this.copyTooltip.disabled = false;
      this.copyTooltip.show();
    } else {
      this.snackBar.open(
        'Failed to copy email template. Please try again.',
        null,
        {
          duration: 2000,
        },
      );
    }

    setTimeout(() => {
      this.copyTooltip.hide();
      this.copyTooltip.disabled = true;
    }, 2000);
  }

  public writeWorkbook(workbook: XLSX.WorkBook, fileName: string): void {
    XLSX.writeFile(workbook, fileName);
  }

  // Builds flattened Excel rows for each incumbent product and its target products
  public createExcelRowData(
    incumbentProducts: XrefRelationshipModel[],
  ): Record<string, any>[] {
    return incumbentProducts.flatMap(incumbentProduct =>
      this.createExcelRowsForIncumbent(incumbentProduct),
    );
  }

  // Creates rows for a single incumbent;
  // One row per target product or a single row with incumbent only when no target exists
  public createExcelRowsForIncumbent({
    incumbent,
    xrefTargets,
  }: XrefRelationshipModel): Record<string, any>[] | Record<string, any> {
    if (xrefTargets?.length > 0) {
      return xrefTargets.map(target => ({
        'Incumbent PIM': incumbent.pimKey ?? '',
        'Incumbent Supplier Name': incumbent.supplierName ?? '',
        'Incumbent Supplier Cat #': incumbent.catalogNumber ?? '',
        'Incumbent Supplier Product Description': incumbent.description ?? '',
        'Competing PIM': target.pimKey ?? '',
        'Competing Supplier Name': target.supplierName ?? '',
        'Competing Supplier Cat #': target.catalogNumber ?? '',
        'Competing Supplier Product Description': target.description ?? '',
        'Supplier Provided Date': target.supplierVerifiedDateTime
          ? new Date(target.supplierVerifiedDateTime).toLocaleDateString()
          : '',
      }));
    }

    // No target products: return single record (not array)
    return {
      'Incumbent PIM': incumbent.pimKey ?? '',
      'Incumbent Supplier Name': incumbent.supplierName ?? '',
      'Incumbent Supplier Cat #': incumbent.catalogNumber ?? '',
      'Incumbent Supplier Product Description': incumbent.description ?? '',
      'Competing PIM': '',
      'Competing Supplier Name': '',
      'Competing Supplier Cat #': '',
      'Competing Supplier Product Description': '',
      'Supplier Provided Date': '',
    };
  }
}
